// Generated from auth/client/ by scripts/sync-auth-client.mjs. Do not edit here.
// Shared sign-in for the tafiti.dev apps.
//
// EDIT THIS FILE HERE (auth/client/). scripts/sync-auth-client.mjs copies it
// into every app; the copies are overwritten on each sync.
//
// The session lives in an httpOnly cookie set by the auth service
// (auth.tafiti.dev) on .tafiti.dev, so one sign-in covers every app and lasts
// 60 days, renewed while you keep using the apps. This module only asks the
// auth service who is signed in; it never sees a token.
//
// Only used on *.tafiti.dev (or when VITE_AUTH_URL is set). Other hosts,
// drlugha.com in particular, keep the previous Google sign-in, because
// drlugha.com decides centrally who can use the apps.

const CACHE_KEY = 'tafiti-auth';
const USER_NAME_KEY = 'polyglot-user-name';
// The previous Google sign-in's keys. Cleared only in shared mode; drlugha.com still uses them.
const LEGACY_KEYS = ['google_auth_tokens', 'polyglot-auth-user'];
const REQUEST_TIMEOUT_MS = 8000;

const configuredUrl = () => (import.meta.env?.VITE_AUTH_URL || '').trim().replace(/\/$/, '');

function isTafitiHost() {
    const host = window.location.hostname;
    return host === 'tafiti.dev' || host.endsWith('.tafiti.dev');
}

/** True when this deployment signs in through the shared auth service. */
export function usesSharedAuth() {
    return isTafitiHost() || !!configuredUrl();
}

/** Where the auth service lives. VITE_AUTH_URL wins (e.g. http://localhost:3100). */
export function authBaseUrl() {
    return configuredUrl() || 'https://auth.tafiti.dev';
}

function readCache() {
    try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        if (cached?.user && cached.expiresAt > Date.now()) return cached;
    } catch {
        // ignore
    }
    return null;
}

function writeCache(value) {
    try {
        if (value) localStorage.setItem(CACHE_KEY, JSON.stringify(value));
        else localStorage.removeItem(CACHE_KEY);
    } catch {
        // storage blocked; we'll just ask the server every time
    }
}

class TafitiAuth {
    constructor() {
        this.user = null;
        this.expiresAt = 0;
        this.listeners = new Set();
        this.ready = null;
    }

    /** Ask the auth service who's signed in. Safe to call repeatedly. */
    initialize() {
        if (!this.ready) {
            LEGACY_KEYS.forEach((key) => {
                try { localStorage.removeItem(key); } catch { /* ignore */ }
            });
            this.ready = this.refresh();
        }
        return this.ready;
    }

    async refresh() {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const response = await fetch(`${authBaseUrl()}/api/session`, {
                credentials: 'include',
                cache: 'no-store',
                signal: controller.signal,
            });
            if (response.ok) {
                const data = await response.json();
                this.set(data.user, data.expiresAt);
            } else if (response.status === 401) {
                this.set(null);
            } else {
                // Server trouble: keep whatever we knew rather than signing people out.
                this.useCache();
            }
        } catch {
            // Offline, or the auth service is unreachable. An installed app should
            // still open for someone who was signed in, so trust the recent answer.
            this.useCache();
        } finally {
            clearTimeout(timer);
        }
        return this.isSignedIn();
    }

    useCache() {
        const cached = readCache();
        this.set(cached?.user || null, cached?.expiresAt, { persist: false });
    }

    set(user, expiresAt = 0, { persist = true } = {}) {
        const wasSignedIn = this.isSignedIn();
        const previousId = this.user?.id;
        this.user = user || null;
        this.expiresAt = user ? expiresAt : 0;
        if (persist) writeCache(user ? { user, expiresAt } : null);
        // Speaker name used by Rooms and Podium; only fill it in, never override.
        if (user) {
            try {
                if (!localStorage.getItem(USER_NAME_KEY)) {
                    localStorage.setItem(USER_NAME_KEY, user.givenName || user.name || '');
                }
            } catch { /* ignore */ }
        }
        if (wasSignedIn !== this.isSignedIn() || previousId !== this.user?.id) {
            this.listeners.forEach((fn) => {
                try { fn(this.user); } catch (error) { console.error('auth listener failed', error); }
            });
        }
    }

    isSignedIn() {
        return !!this.user && this.expiresAt > Date.now();
    }

    /** Send the browser to the sign-in page; it comes back here afterwards. */
    signIn(returnTo = window.location.href) {
        window.location.assign(`${authBaseUrl()}/?return_to=${encodeURIComponent(returnTo)}`);
    }

    async signOut() {
        try {
            await fetch(`${authBaseUrl()}/api/logout`, { method: 'POST', credentials: 'include' });
        } catch (error) {
            console.warn('Sign-out request failed; clearing local state anyway', error);
        }
        this.set(null);
    }

    /** @returns {() => void} unsubscribe */
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
}

export const tafitiAuth = new TafitiAuth();
